<template>
  <div class="fcom_event_single_page">
    <!-- Page header: back link + manager actions -->
    <div class="fhr_content_layout_header">
      <h1 class="fhr_page_title">
        <a class="fcom_back_link" @click.prevent="$emit('go-back')">
          <i class="fcom_icon" v-html="icons.arrowLeft" />
          {{ __('back_to_events') }}
        </a>
      </h1>

      <div class="fhr_page_actions"></div>
    </div>

    <!-- Body area -->
    <div class="fhr_content_layout_body">

      <!-- Loading spinner -->
      <div v-if="loading" style="text-align: center; padding: 60px">
        <div class="fcom_spinner" />
      </div>

      <!-- Recurring event not supported message -->
      <div v-else-if="error === 'recurring_not_supported'" class="fcom_alert fcom_alert_info">
        <div class="fcom_recurring_message">
          <i class="fcom_icon" v-html="icons.calendar" />
          <h3>{{ __('recurring_not_supported_title') }}</h3>
          <p>{{ __('recurring_not_supported_message') }}</p>
          <button class="el-button fcom_secondary_button" @click="$emit('go-back')">
            <i class="fcom_icon" v-html="icons.arrowLeft" />
            {{ __('back_to_events') }}
          </button>
        </div>
      </div>

      <!-- Generic error -->
      <div v-else-if="error" class="fcom_alert fcom_alert_error">
        {{ error }}
      </div>

      <!-- Event loaded -->
      <div v-else-if="event" class="fcom_detail_layout">

        <!-- Main column: image, title, meta, description -->
        <div class="fcom_detail_main">

          <!-- Hero image -->
          <div v-if="event.image" class="fcom_detail_image">
            <img :alt="event.title" :src="event.image" />
          </div>

          <!-- Title -->
          <h2 class="fcom_detail_title">{{ event.title }}</h2>

          <!-- Compact meta row -->
          <div class="fcom_detail_meta">
            <!-- Date -->
            <span v-if="event.date" class="fcom_detail_meta-item">
              <i class="fcom_icon fcom_meta_icon" v-html="icons.calendarFilled" />
              {{ getDateDisplayMid() }}
            </span>

            <span v-if="event.date && event.time_display" class="fcom_detail_meta-sep">·</span>

            <!-- Time -->
            <span v-if="event.time_display" class="fcom_detail_meta-item">
              <i class="fcom_icon fcom_meta_icon" v-html="icons.clockFilled" />
              {{ event.time_display }}
            </span>

            <span v-if="showMetaLocationOrSpaces" class="fcom_detail_meta-sep">·</span>

            <!-- Location (in-person) / online link / locked: short label, full text on hover (title) -->
            <div v-if="showMetaLocationOrSpaces" class="fcom_detail_meta-item">
              <i class="fcom_icon fcom_meta_icon"
                v-html="event.is_online ? icons.monitorFilled : icons.locationFilled" />
              <div class="fcom_detail_meta-item-body">
                <template v-if="event.event_type === 'in_person'">
                  <a v-if="isValidUrl(event.location)" :href="event.location" class="fcom_event_link_btn" rel="noopener"
                    target="_blank">{{ event.location }}</a>
                  <span v-else>{{ event.location }}</span>
                </template>
                <template v-else>
                  <template v-if="canShowJoinMeetingUrl && metaOnlineLinkLabel">
                    <a v-if="isValidUrl(metaOnlineLinkHref)" :href="metaOnlineLinkHref" class="fcom_event_link_btn"
                      rel="noopener" target="_blank">{{ metaOnlineLinkLabel }}</a>
                    <span v-else>{{ metaOnlineLinkLabel }}</span>
                  </template>
                  <template v-else-if="shouldPromptJoinSpaces && meetingLinkSpacesToJoin.length">
                    <span
                      class="fcom_meta_join_disabled"
                      :title="meetingLinkMetaTooltip"
                      :aria-label="meetingLinkMetaTooltip"
                    >
                      <i class="fcom_icon fcom_icon--lock" v-html="icons.lock" />
                      <span class="fcom_meta_join_disabled_text">{{ __('meeting_link_meta_join_label') }}</span>
                    </span>
                  </template>
                  <template
                    v-else-if="meetingTimeLocked && meetingLinkAllowedByMembership && (event.event_url || event.location)">
                    <span class="fcom_link_scheduled_meta">{{ linkNotYetAvailableText }}</span>
                  </template>
                  <template v-else-if="event.location">
                    <a v-if="event.is_online && isValidUrl(event.location)" :href="event.location"
                      class="fcom_event_link_btn" rel="noopener" target="_blank">{{ event.location }}</a>
                    <span v-else>{{ event.location }}</span>
                  </template>
                </template>
              </div>
            </div>

            <span v-if="event.time_display || showMetaLocationOrSpaces" class="fcom_detail_meta-sep">·</span>

            <!-- Event type badge -->
            <span :class="['fcom_detail_type_badge', 'is-' + event.event_type]">
              {{
                event.event_type === 'online'
                  ? __('online')
                  : event.event_type === 'hybrid'
                    ? __('hybrid')
                    : __('in_person')
              }}
            </span>
          </div>

          <!-- Categories -->
          <div v-if="event.categories && event.categories.length" class="fcom_detail_tags">
            <span v-for="cat in event.categories" :key="cat.id || cat" :style="cat.color
              ? { backgroundColor: cat.color + '20', color: cat.color, borderColor: cat.color + '40' }
              : {}" class="fcom_category_tag">
              {{ cat.name || cat }}
            </span>
          </div>

          <!-- Description -->
          <div class="fcom_detail_content_card">
            <h3 v-if="showAboutTitle" class="fcom_detail_section_title">{{ __('about_event') }}</h3>
            <div v-if="event.content" class="fcom_detail_content" v-html="event.content" />
            <p v-else style="color: var(--fcom-secondary-text)">{{ __('no_description') }}</p>
          </div>
        </div>

        <!-- Sidebar column: details + actions -->
        <div class="fcom_detail_sidebar">
          <div class="fcom_sidebar_card">
            <div class="fcom_sidebar_card-title">{{ __('details') }}</div>

            <!-- Date row -->
            <div v-if="event.date" class="fcom_sidebar_row">
              <div class="fcom_sidebar_row-icon">
                <i class="fcom_icon" v-html="icons.calendarFilled" />
              </div>
              <div>
                <div class="fcom_sidebar_row-label">{{ __('date') }}</div>
                <div class="fcom_sidebar_row-value">{{ getDateDisplayLong() }}</div>
              </div>
            </div>

            <!-- Time row -->
            <div v-if="event.time_display" class="fcom_sidebar_row">
              <div class="fcom_sidebar_row-icon">
                <i class="fcom_icon" v-html="icons.clockFilled" />
              </div>
              <div>
                <div class="fcom_sidebar_row-label">{{ __('time') }}</div>
                <div class="fcom_sidebar_row-value">{{ event.time_display }}</div>
              </div>
            </div>

            <!-- Venue row (in-person / hybrid) -->
            <div v-if="event.location && event.event_type !== 'online'" class="fcom_sidebar_row">
              <div class="fcom_sidebar_row-icon">
                <i class="fcom_icon" v-html="icons.locationFilled" />
              </div>
              <div>
                <div class="fcom_sidebar_row-label">{{ __('venue') }}</div>
                <div class="fcom_sidebar_row-value">{{ event.location }}</div>
              </div>
            </div>

            <!-- Online link row -->
            <div v-if="event.event_url && ['online', 'hybrid'].includes(event.event_type)" class="fcom_sidebar_row">
              <div class="fcom_sidebar_row-icon">
                <i class="fcom_icon" v-html="icons.monitorFilled" />
              </div>
              <div>
                <div class="fcom_sidebar_row-label">{{ __('online') }}</div>
                <div class="fcom_sidebar_row-value">
                  <a v-if="canShowJoinMeetingUrl" :href="event.event_url" class="fcom_simple_link" rel="noopener"
                    target="_blank">
                    {{
                      event.meeting_provider
                        ? __f('via_provider', {
                          provider: getMeetingProviderName(event.meeting_provider,
                            event.meeting_provider_other)
                        })
                        : event.event_url
                    }}
                  </a>
                  <div
                    v-else-if="shouldPromptJoinSpaces && meetingLinkSpacesToJoin.length"
                    class="fcom_sidebar_restricted_block"
                  >
                    <div class="fcom_restricted_url_row">
                      <span class="fcom_restricted_url_text">{{ __('meeting_url_restricted') }}</span>
                      <span
                        class="fcom_help_tip"
                        :title="meetingLinkGuidanceTitle"
                        :aria-label="meetingLinkGuidanceTitle"
                        tabindex="0"
                      >
                        <i class="fcom_icon fcom_icon--help" v-html="icons.helpCircle" />
                      </span>
                    </div>
                    <div class="fcom_space_name_links fcom_space_name_links--sidebar fcom_space_name_links--restricted">
                      <template v-for="row in meetingLinkSpacesToJoin" :key="row.id">
                        <a
                          v-if="row.url"
                          :href="row.url"
                          class="fcom_simple_link fcom_simple_link--restricted"
                          rel="noopener"
                          target="_blank"
                        >{{ joinSpaceCtaLabel(row) }}</a>
                        <span v-else class="fcom_space_name_muted">{{ joinSpaceCtaLabel(row) }}</span>
                      </template>
                    </div>
                  </div>
                  <span v-else-if="meetingTimeLocked && meetingLinkAllowedByMembership">
                    {{ linkNotYetAvailableText }}
                  </span>
                  <span v-else>
                    {{
                      event.meeting_provider
                        ? __f('via_provider', {
                          provider: getMeetingProviderName(event.meeting_provider,
                            event.meeting_provider_other)
                        })
                        : ''
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Spaces row -->
            <div v-if="event.spaces && event.spaces.length" class="fcom_sidebar_row">
              <div class="fcom_sidebar_row-icon">
                <i class="fcom_icon" v-html="icons.users" />
              </div>
              <div>
                <div class="fcom_sidebar_row-label">{{ event.spaces.length === 1 ? __('space') : __('spaces') }}</div>
                <div class="fcom_sidebar_row-value fcom_sidebar_spaces">
                  <a v-for="space in event.spaces" :key="space.id"
                    :href="(config.portalBase || '').replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, '') + '/space/' + space.slug"
                    class="fcom_sidebar_space_link">{{ space.title }}</a>
                </div>
              </div>
            </div>

            <!-- Add to calendar + Bookmark + Share buttons -->
            <div class="fcom_sidebar_actions">
              <!-- Add to calendar popover -->
              <div ref="calWrap" class="fcom_cal_popover_wrap">
                <button class="el-button fcom_secondary_button el-button--small"
                  @click="showCalendarPopover = !showCalendarPopover">
                  <i class="fcom_icon" v-html="icons.calendarAdd" />
                  {{ __('add_to_calendar') }}
                </button>

                <div v-if="showCalendarPopover" class="fcom_cal_popover">
                  <a :href="googleCalendarUrl" class="fcom_cal_option" rel="noopener" target="_blank"
                    @click="showCalendarPopover = false">
                    <svg fill="currentColor" height="16" viewBox="0 0 24 24" width="16">
                      <path
                        d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
                    </svg>
                    {{ __('google_calendar') }}
                  </a>
                  <a :href="outlookCalendarUrl" class="fcom_cal_option" rel="noopener" target="_blank"
                    @click="showCalendarPopover = false">
                    <svg fill="currentColor" height="16" viewBox="0 0 24 24" width="16">
                      <rect fill="#0078d4" height="8" rx="1" width="8" x="3" y="3" />
                      <rect fill="#0078d4" height="8" opacity=".6" rx="1" width="8" x="13" y="3" />
                      <rect fill="#0078d4" height="8" opacity=".6" rx="1" width="8" x="3" y="13" />
                      <rect fill="#0078d4" height="8" opacity=".4" rx="1" width="8" x="13" y="13" />
                    </svg>
                    {{ __('outlook') }}
                  </a>
                  <a :href="yahooCalendarUrl" class="fcom_cal_option" rel="noopener" target="_blank"
                    @click="showCalendarPopover = false">
                    <svg fill="#5f01d1" height="16" viewBox="0 0 24 24" width="16">
                      <path d="M12 2L8.5 9.5 5 2H1.5l5.5 11v7h2v-7L14.5 2H12zm2.5 0l3.5 7 3.5-7H14.5z" />
                    </svg>
                    {{ __('yahoo') }}
                  </a>
                  <button class="fcom_cal_option" @click="downloadIcs">
                    <svg fill="none" height="16" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="1.5" viewBox="0 0 24 24" width="16">
                      <rect height="18" rx="2" width="18" x="3" y="4" />
                      <line x1="16" x2="16" y1="2" y2="6" />
                      <line x1="8" x2="8" y1="2" y2="6" />
                      <line x1="3" x2="21" y1="10" y2="10" />
                      <path d="M12 14v4m-2-2h4" />
                    </svg>
                    {{ __('apple_ics') }}
                  </button>
                </div>
              </div>

              <!-- Bookmark / Save button -->
              <button
                :class="['el-button fcom_secondary_button el-button--small', { fcom_btn_save_active: event.is_saved }]"
                @click="toggleBookmark">
                <i class="fcom_icon" v-html="event.is_saved ? icons.bookmarkFilled : icons.bookmark" />
                {{ event.is_saved ? __('saved') : __('save') }}
              </button>

              <!-- Share button -->
              <button v-if="showShareButton" class="el-button fcom_secondary_button el-button--small"
                @click="openShareModal">
                <i class="fcom_icon" v-html="icons.share" />
                {{ __('share') }}
              </button>
            </div>
          </div>

          <!-- Admin actions -->
          <div v-if="canManage" class="fcom_sidebar_admin_actions">
            <button class="fcom_sidebar_action_btn" @click="$emit('edit-event', event.slug, event.id)">
              <i class="fcom_icon" v-html="icons.edit" />
              <span>{{ __('edit_event') }}</span>
            </button>
            <button class="fcom_sidebar_action_btn fcom_sidebar_action_btn--danger" @click="confirmDelete">
              <i class="fcom_icon" v-html="icons.trash" />
              <span>{{ __('delete_event') }}</span>
            </button>
          </div>

        </div>
      </div>

      <!-- Event not found (after loading, no error, no event) -->
      <div v-else class="fcom_empty_state">
        <i class="fcom_icon" v-html="icons.calendarFilled" />
        <h3>{{ __('event_not_found') }}</h3>
      </div>

    </div>
  </div>

  <!-- Share modal (teleported to body) -->
  <ShareModal :show="showShareModal" :summary="event?.summary || ''" :title="event?.title || ''" :url="currentUrl"
    @close="showShareModal = false" />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { DateTime } from 'luxon'
import { __, __f } from '@/utils/i18n.js'
import icons from '@/utils/icons.js'
import ShareModal from '@/components/ShareModal.vue'

// ── Props / Emits ────────────────────────────────────────────────────────────

const props = defineProps({
  slug: { type: String, required: true },
})

const emit = defineEmits(['go-back', 'edit-event', 'deleted'])

// ── Config ───────────────────────────────────────────────────────────────────

const config = window.FcaEventsConfig || {}

// ── Reactive state ───────────────────────────────────────────────────────────

const canManage = ref((config.canManage) || false)
const loading = ref(true)
const error = ref(null)
const event = ref(null)
const showShareModal = ref(false)
const showCalendarPopover = ref(false)
const calWrap = ref(null)

// ── Computed ─────────────────────────────────────────────────────────────────

const showShareButton = computed(() => (config.settings || {}).show_share_button !== false)
const showAboutTitle = computed(() => (config.settings || {}).show_about_title !== false)

/**
 * Unlocks the join URL on schedule: if link_visible_hours_before is set, hidden until
 * that many hours before the event start (UTC).
 */
const joinLinkUnlockedByTime = computed(() => {
  if (!event.value) return false
  if (!['online', 'hybrid'].includes(event.value.event_type)) return false
  if (!event.value.event_url) return false

  const hoursBefore = event.value.link_visible_hours_before
  if (!hoursBefore || hoursBefore <= 0) return true

  const startUtc = event.value.date
  if (!startUtc) return true

  const eventStart = DateTime.fromISO(startUtc.replace(' ', 'T'), { zone: 'UTC' })
  if (!eventStart.isValid) return true

  const visibleFrom = eventStart.minus({ hours: hoursBefore })
  return DateTime.now() >= visibleFrom
})

/** API: can this user see the meeting URL given meeting_space_ids membership rules? */
const meetingLinkAllowedByMembership = computed(() => {
  const e = event.value
  if (!e) return false
  const ids = e.meeting_space_ids || []
  if (ids.length === 0) return true
  return e.is_visible === true
})

/** Show the real meeting join URL (not space fallback): allowed by space + on schedule. */
const canShowJoinMeetingUrl = computed(() => {
  if (!event.value) return false
  if (!['online', 'hybrid'].includes(event.value.event_type)) return false
  if (!event.value.event_url) return false
  return meetingLinkAllowedByMembership.value && joinLinkUnlockedByTime.value
})

/** No membership (or guest): show space name + link from linkStatus so user can open the space. */
const shouldPromptJoinSpaces = computed(() => {
  const e = event.value
  if (!e) return false
  return (e.meeting_space_ids || []).length > 0 && e.is_visible === false
})

/**
 * Subset of `linkStatus`: spaces the user is not a member of yet.
 * Hides "Join …" for spaces already joined (notably in "all spaces" mode after partial join).
 */
const meetingLinkSpacesToJoin = computed(() => {
  const e = event.value
  if (!e || !Array.isArray(e.linkStatus)) return []
  return e.linkStatus.filter((row) => !row.is_visible)
})

/** Sidebar / CTA: long guidance for tooltips. */
const meetingLinkGuidanceTitle = computed(() => {
  const e = event.value
  if (!e) return __('meeting_link_join_space_guidance')
  const n = (e.meeting_space_ids || []).length
  if (n > 1 && e.meeting_space_member_mode === 'all') {
    return __('meeting_link_join_space_guidance_all')
  }
  return __('meeting_link_join_space_guidance')
})

/** Meta row only: short line + space names (no links in tooltip). */
const meetingLinkMetaTooltip = computed(() => {
  const rows = meetingLinkSpacesToJoin.value
  if (!rows.length) return ''
  const e = event.value
  if (!e) return ''
  const names = rows.map((r) => r.name || __f('space_label_fallback', { id: r.id })).join(', ')
  const n = (e.meeting_space_ids || []).length
  const intro =
    n > 1 && e.meeting_space_member_mode === 'all'
      ? __('meeting_link_meta_tooltip_intro_all')
      : __('meeting_link_meta_tooltip_intro')
  return `${intro} ${names}`
})

function joinSpaceCtaLabel(row) {
  return __f('join_space_cta', { space: row.name || __f('space_label_fallback', { id: row.id }) })
}

const meetingTimeLocked = computed(() => !joinLinkUnlockedByTime.value)

const showMetaLocationOrSpaces = computed(() => {
  const e = event.value
  if (!e) return false
  if (e.event_type === 'in_person') return !!e.location
  if (['online', 'hybrid'].includes(e.event_type) || e.is_online) {
    if (e.location) return true
    if (e.event_url) return true
    if ((e.meeting_space_ids || []).length > 0) return true
  }
  return false
})

const metaOnlineLinkHref = computed(() => {
  const e = event.value
  if (!e) return ''
  if (e.event_url) return e.event_url
  if (e.location) {
    try {
      new URL(e.location)
      return e.location
    } catch { /* not a URL */ }
  }
  return ''
})

const metaOnlineLinkLabel = computed(() => {
  const e = event.value
  if (!e) return ''
  if (e.meeting_provider) {
    return __f('via_provider', {
      provider: getMeetingProviderName(e.meeting_provider, e.meeting_provider_other)
    })
  }
  if (e.location) return e.location
  return e.event_url || ''
})

/** Text shown on the disabled join button when link is not yet visible. */
const linkNotYetAvailableText = computed(() => {
  if (!event.value) return ''
  return __f('link_available_hours_before', { hours: event.value.link_visible_hours_before || 1 })
})

/** Google Calendar add-event URL. */
const googleCalendarUrl = computed(() => {
  if (!event.value) return '#'
  const start = formatDateForCalendar(event.value.date)
  const end = formatDateForCalendar(event.value.end_time) || start
  return 'https://calendar.google.com/calendar/render?' + new URLSearchParams({
    action: 'TEMPLATE',
    text: event.value.title || '',
    dates: `${start}/${end}`,
    details: event.value.summary || '',
    location: event.value.location || (event.value.is_online ? event.value.event_url : '') || '',
    trp: 'false',
  })
})

/** Outlook live calendar add-event URL. */
const outlookCalendarUrl = computed(() => {
  if (!event.value) return '#'
  const toOutlook = (str) => str ? str.replace(' ', 'T') + (str.includes('Z') ? '' : 'Z') : ''
  return 'https://outlook.live.com/calendar/0/deeplink/compose?' + new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.value.title || '',
    startdt: toOutlook(event.value.date),
    enddt: toOutlook(event.value.end_time || event.value.date),
    body: event.value.summary || '',
    location: event.value.location || '',
  })
})

/** Yahoo Calendar add-event URL. */
const yahooCalendarUrl = computed(() => {
  if (!event.value) return '#'
  const start = formatDateForCalendar(event.value.date)
  const end = formatDateForCalendar(event.value.end_time) || start
  return 'https://calendar.yahoo.com/?' + new URLSearchParams({
    v: '60',
    title: event.value.title || '',
    st: start,
    et: end,
    desc: event.value.summary || '',
    in_loc: event.value.location || '',
  })
})

/** Current page URL, passed to ShareModal. */
const currentUrl = computed(() => window.location.href)

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns long date display string (e.g. "Friday, March 28, 2025"). */
function getDateDisplayLong() {
  return event.value?.date_display_long || event.value?.date_display || ''
}

/** Returns medium date display string (e.g. "Mar 28"). */
function getDateDisplayMid() {
  return event.value?.date_display_mid || event.value?.date_display || ''
}

/**
 * Returns the human-readable name for a meeting provider.
 * If type is "other" and a custom name was provided, use that.
 */
function getMeetingProviderName(type, customName) {
  if (type === 'other' && customName) return customName
  const names = {
    zoom: __('zoom'),
    google_meet: __('google_meet'),
    microsoft_teams: __('microsoft_teams'),
    webex: __('webex'),
    other: __('other'),
  }
  return names[type] || type
}

/** Checks whether a string is a valid URL. */
function isValidUrl(str) {
  if (!str) return false
  try {
    new URL(str)
    return true
  } catch {
    return false
  }
}

/**
 * Converts a datetime string to the compact format required by Google/Yahoo
 * calendar URL parameters (e.g. "20250328T140000Z").
 */
function formatDateForCalendar(dateStr) {
  if (!dateStr) return ''
  return dateStr.replace(' ', 'T').replace(/[-:]/g, '').slice(0, 15) + 'Z'
}

/**
 * Parses a datetime string (space- or T-separated) as UTC using Luxon.
 * Returns a DateTime or null if invalid.
 */
function parseDateUtc(str) {
  if (!str) return null
  const dt = DateTime.fromISO(str.replace(' ', 'T'), { zone: 'utc' })
  return dt.isValid ? dt : null
}

/**
 * Generates an ICS file string for the event and triggers a browser download.
 */
function generateIcsContent(ev) {
  const start = parseDateUtc(ev.date)
  const end = parseDateUtc(ev.end_time)
  if (!start) return null

  const fmt = (dt) => dt.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'")
  const escape = (str) =>
    (str || '').replace(/[,;\\]/g, (ch) => '\\' + ch).replace(/\n/g, '\\n')

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FCA Events Basic//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end || start.plus({ hours: 1 }))}`,
    `SUMMARY:${escape(ev.title)}`,
    `LOCATION:${escape(ev.location)}`,
    `DESCRIPTION:${escape(ev.summary)}`,
    `URL:${window.location.href}`,
    `UID:fca-event-${ev.id}@${window.location.hostname}`,
    `DTSTAMP:${fmt(DateTime.utc())}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')
}

// ── Actions ───────────────────────────────────────────────────────────────────

/** Fetches the event data from the REST API by slug. */
async function fetchEvent() {
  loading.value = true
  error.value = null
  try {
    const res = await fetch(`${config.apiBase}events/${props.slug}`, {
      headers: { 'X-WP-Nonce': config.nonce },
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      if (body.code === 'fca_event_not_found' && props.slug.includes('-occurrence-')) {
        error.value = 'recurring_not_supported'
        return
      }
      throw new Error(`HTTP ${res.status}`)
    }

    const data = await res.json()

    // Recurring events are not supported in this view
    if (data.is_recurring || data.rrule || (data.parent_event_id && data.parent_event_id !== data.id)) {
      error.value = 'recurring_not_supported'
      return
    }

    event.value = data
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

/** Triggers an ICS file download for the current event. */
function downloadIcs() {
  if (!event.value) return
  showCalendarPopover.value = false
  const icsContent = generateIcsContent(event.value)
  if (!icsContent) return
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(blob)
  anchor.download = (event.value.slug || 'event') + '.ics'
  anchor.click()
  URL.revokeObjectURL(anchor.href)
}

/** Toggles the bookmark/save state for the current event. */
async function toggleBookmark() {
  if (!event.value) return
  try {
    await fetch(`${config.apiBase}${event.value.is_saved ? 'unsave' : 'save'}`, {
      method: 'POST',
      headers: { 'X-WP-Nonce': config.nonce, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: event.value.id }),
    })
    event.value.is_saved = !event.value.is_saved
  } catch {
    // silently ignore bookmark errors
  }
}

/** Opens the share modal. */
function openShareModal() {
  showShareModal.value = true
}

/** Prompts the user to confirm deletion, then calls deleteEvent(). */
function confirmDelete() {
  if (event.value && confirm(__('confirm_delete'))) {
    deleteEvent()
  }
}

/** Sends a DELETE request and emits 'deleted' on success. */
async function deleteEvent() {
  if (!event.value) return
  try {
    const res = await fetch(`${config.apiBase}events/${event.value.id}`, {
      method: 'DELETE',
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    emit('deleted')
  } catch (err) {
    error.value = err.message
  }
}

/**
 * Closes the calendar popover when the user clicks outside the calWrap element.
 * Attached as a capture-phase listener so it fires before any click handlers.
 */
function handleOutsideClick(evt) {
  if (calWrap.value && !calWrap.value.contains(evt.target)) {
    showCalendarPopover.value = false
  }
}

// ── Watchers ─────────────────────────────────────────────────────────────────

// Re-fetch whenever the slug prop changes
watch(
  () => props.slug,
  (newSlug) => {
    if (newSlug) fetchEvent()
  },
)

// Update the browser tab title when the event title loads
watch(
  () => event.value?.title,
  (title) => {
    if (title) {
      const siteTitle = window.fluentComAdmin?.site_title || document.title
      document.title = title + ' - ' + siteTitle
    }
  },
)

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  if (props.slug) fetchEvent()
  document.addEventListener('click', handleOutsideClick, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick, true)
})
</script>

<style scoped>
.fcom_detail_meta-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.fcom_detail_meta-item-body {
  min-width: 0;
  flex: 1;
}

.fcom_meta_join_disabled {
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  padding: 0.15rem 0.45rem 0.15rem 0.35rem;
  line-height: 1.2;
  border-radius: 4px;
  cursor: help;
  user-select: none;
  color: var(--fcom-secondary-text, #6b6f76);
  background: color-mix(in srgb, var(--fcom-secondary-text, #6b6f76) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--fcom-secondary-text, #6b6f76) 18%, transparent);
  opacity: 0.95;
}

.fcom_meta_join_disabled_text {
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.fcom_icon--lock,
.fcom_icon--help {
  display: inline-flex;
  width: 1em;
  height: 1em;
  flex-shrink: 0;
  vertical-align: middle;
}

.fcom_icon--lock {
  opacity: 0.85;
}

.fcom_sidebar_restricted_block {
  margin-top: 0.35rem;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  color: var(--fcom-secondary-text, #6b6f76);
  background: color-mix(in srgb, var(--fcom-secondary-text, #6b6f76) 7%, transparent);
  border: 1px solid color-mix(in srgb, var(--fcom-secondary-text, #6b6f76) 16%, transparent);
}

.fcom_restricted_url_row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
}

.fcom_restricted_url_text {
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--fcom-secondary-text, #6b6f76);
}

.fcom_help_tip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  cursor: help;
  color: var(--fcom-text-off, #8a8d91);
  border-radius: 999px;
}

.fcom_help_tip:hover,
.fcom_help_tip:focus-visible {
  color: var(--fcom-secondary-text, #6b6f76);
}

.fcom_help_tip:focus-visible {
  outline: 2px solid var(--fcom-primary-button, #1877f2);
  outline-offset: 2px;
}

.fcom_space_name_links--restricted {
  margin-top: 0.5rem;
}

.fcom_space_name_links--sidebar {
  margin-top: 0.45rem;
}

.fcom_simple_link--restricted {
  color: var(--fcom-text-off, #8a8d91) !important;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--fcom-text-off, #8a8d91) 55%, transparent);
}

.fcom_simple_link--restricted:hover {
  color: var(--fcom-secondary-text, #6b6f76) !important;
}

.fcom_space_name_muted {
  font-size: 14px;
  line-height: 1.4;
  color: var(--fcom-text-off, #8a8d91);
  word-break: break-word;
}

.fcom_space_name_links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.6rem;
  align-items: center;
}

</style>
